import React, { useCallback, useEffect, useMemo, useState } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import FLFCustomerControlPanel from "../../components/ControlPanel/FLFCustomerControlPanel";
import Pagination from "src/components/Pagination/Pagination";
import {
  NotifyClearCache,
  NotifyConfirm,
  Toast,
  ToastSyncData,
} from "src/components/notify/Notify";
import CustomerTable from "../../components/Table/CustomerTable";
import RightSideModel from "src/components/popup/rightSide/RightSideModel";
import { dataStateType } from "src/constants/status";
import {
  clearCacheFLFCustomer,
  getFLFCustomer,
  getFLFCustomerDetail,
  showDeviceForCustomer,
  syncDataFLFCustomer,
  updateFLFCustomer,
} from "src/apis/FLF/customer.api";
import { path } from "src/constants/path";
import { CACHE_CUSTOMER } from "src/constants/actionCache";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import Controls from "src/components/form/controls/Controls";
import { getFLFRogoDevType } from "src/apis/FLF/rogoDevType.api";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const initCustomerData = {
  list: [],
  total_record: 0,
};

const initRogoDevTypeData = {
  list: [],
  total_record: 0,
};

export const statusCode = {
  active: 0,
  block: 2,
};

const IS_DISPLAY_DEVICE = {
  SHOW: 1,
  HIDE: 0,
  BLOCK: 2
}

const titlePage = "FLF Customer";

const FLFCustomer = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const permissionsAccessControl = useGetPermissionAccessControl();
  const [customerData, setCustomerData] = useState(initCustomerData);
  const [rogoDevTypeData, setRogoDevTypeData] = useState(initRogoDevTypeData);
  const [rogoDevTypeCodes, setrogoDevTypeCodes] = useState([]);
  const [isDisplayDevice, setIsDisplayDevice] = useState(IS_DISPLAY_DEVICE.SHOW);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [openPopupShowDevice, setOpenPopupShowDevice] = useState(false);
  const [customerForPopupShowDevice, setCustomerForPopupShowDevice] = useState(null);

  const fetchCustomers = useCallback(() => {
    getFLFCustomer({ page }, (result) => {
      setCustomerData({ ...result });
      setFilterCustomers([...result.list]);
    });
  }, [page]);

  const fetchRogoDevType = useCallback(() => {
    getFLFRogoDevType({}, (result) => {
      setRogoDevTypeData({ ...result });
    });
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchRogoDevType();
  }, [fetchCustomers, fetchRogoDevType]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredCustomer = useCallback((customer) => {
    setFilterCustomers(customer);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (customers, key) => {
      const search = searchKey.toLowerCase().trim();
      if (status === dataStateType.INACTIVE) {
        return [];
      } else if (status === dataStateType.BLOCK) {
        customers = customers.filter(
          (customer) => customer.isDisabled === statusCode.block
        );
      } else {
        customers = customers.filter(
          (customer) => customer.isDisabled === statusCode.active
        );
      }
      return customers.filter((customer) => {
        const customerName = (customer.name || "")
          .toString()
          .toLowerCase()
          .trim();
        const fptId = (customer.fptId || "").toString().toLowerCase().trim();
        const phoneNumber = (customer.phoneNumber || "")
          .toString()
          .toLowerCase()
          .trim();

        return (
          customerName.indexOf(search) !== -1 ||
          fptId.indexOf(search) !== -1 ||
          phoneNumber.indexOf(search) !== -1
        );
      });
    },
    [searchKey, status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredCustomers = value
        ? searchByKeyWord(customerData.list, value)
        : customerData.list;
      updateFilteredCustomer(filteredCustomers);
    },
    [customerData.list, searchByKeyWord, updateFilteredCustomer]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  const updateSyncStatus = (id, syncStatus) => {
    const customerList = customerData.list.map((customer) => {
      if (customer.id === id) {
        return { ...customer, syncStatus: syncStatus };
      }
      return customer;
    });

    setCustomerData((prev) => {
      return {
        ...prev,
        list: [...customerList],
      };
    });
  };

  const getStatus = (id) => {
    getFLFCustomerDetail(id, (result) => {
      const { syncStatus } = result;
      if (syncStatus === "success") {
        updateSyncStatus(id, "success");
        Toast("success", "Đồng Bộ Thành Công");
      } else if (syncStatus === "fail") {
        updateSyncStatus(id, "fail");
        Toast("warning", "Đồng Bộ Thất Bại");
      } else {
        setTimeout(() => {
          getStatus(id);
        }, 1000);
      }
    });
  };

  const handleSyncData = (customer) => {
    NotifyConfirm(
      "warning",
      `Người dùng: ${customer.name}`,
      (result) => {
        if (result.isConfirmed) {
          syncDataFLFCustomer(
            { phoneNumber: customer.phoneNumber },
            (result) => {
              ToastSyncData();
              updateSyncStatus(customer.id, "pending");
              setTimeout(() => {
                getStatus(customer.id);
              }, 2000);
            }
          );
        }
      },
      "Đồng Bộ Dữ Liệu ?"
    );
  };

  const handleChangeStatus = (item) => (event) => {
    const { value } = event.target;

    NotifyConfirm("warning", "Are you change it ?", (result) => {
      if (result.isConfirmed) {
        const foundCustomerIndex = customerData.list.findIndex(
          (customer) => customer.id === item.id
        );
        const status =
          value === "active" ? statusCode.active : statusCode.block;

        if (foundCustomerIndex !== -1) {
          // call api update
          updateFLFCustomer(
            item.id,
            {
              status,
            },
            (result) => {
              setCurrentActionCache(CACHE_CUSTOMER);
              // callback nhận về lỗi form và thành công
              if (result.data?.status === 422) {
                Toast("warning", "Something went wrong !");
              } else {
                fetchCustomers();
              }
            }
          );
        }
      }
    });
  };

  const handleRefreshCache = () => {
    NotifyClearCache(
      "warning",
      "Refresh Cache",
      "<h5> làm mới để đảm bảo dữ liệu mới </h5>",
      (result) => {
        if (result.isConfirmed) {
          clearCacheFLFCustomer((result) => {
            window.location.reload(true);
          });
        }
      }
    );
  };

  const handlePopupShowDevice = (customer) => {
    setOpenPopupShowDevice(true);
    setCustomerForPopupShowDevice(customer);
    setrogoDevTypeCodes([]);
    setIsDisplayDevice(IS_DISPLAY_DEVICE.SHOW);
  };

  const handleSubmitShowDevice = () => {
    const customer = customerForPopupShowDevice || {};

    const payload = {
      flfCustomerId: customer.id,
      rogoDevTypeCodes: rogoDevTypeCodes || [],
      isDisplay: isDisplayDevice,
    };

  
    showDeviceForCustomer(payload, (result) => {
      Toast("success", "Thành công");
      setOpenPopupShowDevice(false);
    });

  };

  /* ---- result for render ----- */

  const resultFilter = useMemo(
    () => searchByKeyWord(filterCustomers, searchKey),
    [filterCustomers, searchByKeyWord, searchKey]
  );
  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      <FLFCustomerControlPanel
        currentActionCache={currentActionCache}
        permissionsAccessControl={permissionsAccessControl}
        handleRefreshCache={handleRefreshCache}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFCustomerControlPanel>

      <CustomerTable
        data={listItem}
        handleSyncData={handleSyncData}
        handleChangeStatus={handleChangeStatus}
        handlePopupShowDevice={handlePopupShowDevice}
        status={dataStateType.ACTIVE}
      />

      <Pagination
        pathname={path.flfCustomer}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />

      <RightSideModel
        openPopup={openPopupShowDevice}
        setOpenPopup={setOpenPopupShowDevice}
        title={`Cài đặt hiển thị thiết bị cho người dùng ${customerForPopupShowDevice?.username || ""}`}
      >
        <div>
          <Accordion
              sx={{
                borderRadius: "4px",
                margin: "10px 0",
                padding: "3px 0px",
                background: "unset",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>RogoDevTypes</Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                <Divider />
                <Controls.MultipleCheckbox
                  name="rogoDevTypeCodes"
                  label="RogoDevTypes"
                  value={rogoDevTypeCodes}
                  onChange={(e) => setrogoDevTypeCodes(e.target.value)}
                  options={rogoDevTypeData.list.map((item) => ({
                    ...item,
                    id: Number(item.devTypeCode),
                    name: `${item.name} - ${item.devTypeCode}`,
                  }))}
                />
              </AccordionDetails>
          </Accordion>

          <Controls.Select
            name="isDisplayDevice"
            label="Display device"
            value={isDisplayDevice}
            onChange={(e) => setIsDisplayDevice(e.target.value)}
            options={[
              { id: IS_DISPLAY_DEVICE.SHOW, name: "Show" },
              { id: IS_DISPLAY_DEVICE.HIDE, name: "Hide" },
              { id: IS_DISPLAY_DEVICE.BLOCK, name: "Block" },
            ]}
          />

          <Controls.Button
            type="submit"
            sx={{ width: "100%", marginTop: "10px" }}
            text="Submit"
            onClick={handleSubmitShowDevice}
            disabled={rogoDevTypeCodes.length === 0}
          />
        </div>
      </RightSideModel>
    </div>
  );
};

export default FLFCustomer;
